// Awizacja import — mapper (Excel raw rows → transaction candidates).
//
// THIS is the file to edit when the mapping or matching rules change — it is
// pure logic (company + product reference sets + learned aliases injected).
// Resolution order per company field:
//   learned alias (by name) → NIP → departure address (load place) → fuzzy name.
// A matched company whose NIP / address differs from the Excel raises a
// per-field detail-mismatch warning (matched, but verify).

import { tokenizeName } from "../mbank/matching/tokenizeName.ts";
import { aliasKey, loadPlaceAliasName } from "./AwizacjaAliasStore.ts";
import type { CompanyRec, ProductRec } from "./AwizacjaImportDao.ts";
import type {
  RawAwizacjaRow, TransactionCandidate, CandidateCarrierLeg, MatchState, AliasMap,
  AwizacjaImportResult, CompanyLabel, ProductLabel,
} from "./awizacjaTypes.ts";
import { EMPTY_ALIASES } from "./awizacjaTypes.ts";

const norm = (s: string | null | undefined) =>
  (s ?? "").toLowerCase().replace(/[.,/–-]/g, " ").replace(/\s+/g, " ").trim();
const normNip = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "");
const streetTokens = (s: string | null | undefined) =>
  norm(s).split(" ").filter((t) => t.length > 2 && !["ul", "ulica", "al", "aleja"].includes(t));

function jaccard(a: string[], b: string[]): number {
  if (!a.length || !b.length) return 0;
  const sa = new Set(a), sb = new Set(b);
  let inter = 0;
  for (const x of sa) if (sb.has(x)) inter++;
  return inter / (sa.size + sb.size - inter);
}

interface Resolved { id: number; match: MatchState }

export class AwizacjaMapper {
  private readonly byId: Map<number, CompanyRec>;
  private readonly prodById: Map<number, ProductRec>;
  private readonly peuteId: number | null;

  constructor(
    private readonly companies: CompanyRec[],
    private readonly products: ProductRec[],
    private readonly aliases: AliasMap = EMPTY_ALIASES,
  ) {
    this.byId = new Map(companies.map((c) => [c.id, c]));
    this.prodById = new Map(products.map((p) => [p.id, p]));
    this.peuteId = companies.find((c) => norm(c.short_name).includes("peute") || norm(c.full_name).includes("peute"))?.id ?? null;
  }

  private matchProduct(desc: string): Resolved | null {
    const d = norm(desc);
    if (!d) return null;
    const wants = d.includes("occ") || d.includes("carton") || d.includes("cardboard") || d.includes("karton")
      ? this.products.filter((p) => p.basel_code === "B3020")
      : this.products.filter((p) => norm(p.name_pl).includes(d) || norm(p.name).includes(d));
    return wants.length ? { id: wants[0].id, match: "fuzzy" } : null;
  }

  private alias(kind: keyof AliasMap, name: string): number | null {
    const id = this.aliases[kind]?.[aliasKey(name)];
    return id != null && this.byId.has(id) ? id : null;
  }

  // NIP → learned name alias → fuzzy name.
  private matchCompany(name: string, nip: string | null, category?: string): Resolved | null {
    const cleanNip = normNip(nip);
    if (cleanNip) {
      const byNip = this.companies.find((c) => normNip(c.nip) === cleanNip);
      if (byNip) return { id: byNip.id, match: "exact" };
    }
    const aliasId = this.alias("name", name);
    if (aliasId != null) return { id: aliasId, match: "alias" };
    const tokens = tokenizeName(name);
    if (!tokens.length) return null;
    const pool = category ? this.companies.filter((c) => (c.categories ?? []).includes(category)) : this.companies;
    let best: CompanyRec | null = null, bestScore = 0;
    for (const c of pool) {
      const score = Math.max(jaccard(tokens, tokenizeName(c.full_name ?? "")), jaccard(tokens, tokenizeName(c.short_name ?? "")));
      if (score > bestScore) { bestScore = score; best = c; }
    }
    return best && bestScore >= 0.4 ? { id: best.id, match: "fuzzy" } : null;
  }

  // Learned alias (by departure ADDRESS — the vendor name repeats across
  // sites, the address doesn't) → address scoring.
  private matchLoadPlace(zip: string, city: string, street: string): Resolved | null {
    const aliasId = this.alias("address", loadPlaceAliasName(zip, city, street));
    if (aliasId != null) return { id: aliasId, match: "alias" };
    const pool = this.companies.filter((c) => (c.categories ?? []).includes("waste_generator"));
    let best: CompanyRec | null = null, bestScore = 0;
    for (const c of pool) {
      let score = 0;
      if (c.zip_code && norm(c.zip_code) === norm(zip)) score += 2;
      if (c.city && norm(c.city) === norm(city)) score += 2;
      const have = new Set(streetTokens(c.street));
      const overlap = streetTokens(street).filter((t) => have.has(t)).length;
      if (overlap > 0) score += 2 + overlap;
      if (score > bestScore) { bestScore = score; best = c; }
    }
    if (!best || bestScore < 4) return null;
    return { id: best.id, match: bestScore >= 6 ? "exact" : "fuzzy" };
  }

  // Matched company, but a supplied detail differs → verify warning.
  private nipMismatch(id: number, nip: string | null): string | null {
    const c = this.byId.get(id);
    const inc = normNip(nip);
    if (!c || !inc || !c.nip || normNip(c.nip) === inc) return null;
    return `Dopasowano „${c.short_name}", ale NIP z Excela (${nip}) różni się od bazy (${c.nip}).`;
  }
  private addressMismatch(id: number, zip: string, city: string): string | null {
    const c = this.byId.get(id);
    if (!c) return null;
    const zipOff = zip && c.zip_code && norm(zip) !== norm(c.zip_code);
    const cityOff = city && c.city && norm(city) !== norm(c.city);
    if (!zipOff && !cityOff) return null;
    return `Dopasowano „${c.short_name}", ale adres z Excela (${[zip, city].filter(Boolean).join(" ")}) różni się od bazy (${[c.zip_code, c.city].filter(Boolean).join(" ")}).`;
  }

  mapAll(raws: RawAwizacjaRow[]): AwizacjaImportResult {
    const companyLabels: Record<number, CompanyLabel> = {};
    const productLabels: Record<number, ProductLabel> = {};
    const noteCompany = (id: number | null) => {
      if (id == null || companyLabels[id]) return;
      const c = this.byId.get(id);
      if (c) companyLabels[id] = { short_name: c.short_name ?? `#${id}`, city: c.city, nip: c.nip };
    };
    const noteProduct = (id: number | null) => {
      if (id == null || productLabels[id]) return;
      const p = this.prodById.get(id);
      if (p) productLabels[id] = { name: p.name ?? `#${id}`, waste_code: p.waste_code, basel_code: p.basel_code };
    };

    // One transport reference can span several Tab.1 lines (a multi-container
    // shipment). The review grid keys every edit by `id`, so siblings must get
    // distinct ids; the first occurrence keeps the bare reference.
    const refSeen = new Map<string, number>();
    const rowId = (ref: string) => {
      const n = (refSeen.get(ref) ?? 0) + 1;
      refSeen.set(ref, n);
      return n === 1 ? ref : `${ref}#${n}`;
    };

    const candidates: TransactionCandidate[] = raws.map((r) => {
      const product = this.matchProduct(r.itemDesc);
      const loadPlace = this.matchLoadPlace(r.depZip, r.depCity, r.depStreet);
      const unloadPlace = this.matchCompany(r.recoveryFacility, null, "recycler");
      const importerId = unloadPlace ? (this.byId.get(unloadPlace.id)?.parent_company_id ?? null) : null;

      const matchWarnings: { field: string; message: string }[] = [];
      if (loadPlace) { const m = this.addressMismatch(loadPlace.id, r.depZip, r.depCity); if (m) matchWarnings.push({ field: "loadPlace", message: m }); }

      const carriers: CandidateCarrierLeg[] = r.carriers.map((leg) => {
        const m = this.matchCompany(leg.name, leg.nip, "transporter");
        if (m) { const mm = this.nipMismatch(m.id, leg.nip); if (mm) matchWarnings.push({ field: `carrier-${leg.role}`, message: mm }); }
        return {
          role: leg.role, rawName: leg.name, nip: leg.nip, driver: leg.driver,
          driverPhone: leg.driverPhone, plate: leg.plate, means: leg.means,
          companyId: m?.id ?? null, match: m?.match ?? "unmatched",
        };
      });

      noteProduct(product?.id ?? null);
      [loadPlace?.id ?? null, unloadPlace?.id ?? null, importerId, this.peuteId].forEach(noteCompany);
      carriers.forEach((leg) => noteCompany(leg.companyId));

      const date = r.collectionDate ?? "";
      return {
        id: rowId(r.peuteRef), sourceFile: r.sourceFile, externalId: r.peuteRef, orderNumber: r.orderNumber,
        container: r.container, size: r.size, shipowner: r.shipowner,
        loadDate: date, unloadDate: date, timeslot: r.timeslot,
        productRaw: r.itemDesc, productId: product?.id ?? null, productMatch: product?.match ?? "unmatched",
        loadPlaceRaw: r.vendorName, loadPlaceId: loadPlace?.id ?? null, loadPlaceMatch: loadPlace?.match ?? "unmatched",
        depCountry: r.depCountry, depZip: r.depZip, depCity: r.depCity, depStreet: r.depStreet,
        unloadPlaceRaw: r.recoveryFacility, unloadPlaceId: unloadPlace?.id ?? null, unloadPlaceMatch: unloadPlace?.match ?? "unmatched",
        importerId, unloadCommercialId: this.peuteId,
        carriers, matchWarnings, createdRef: null, createdId: null,
      };
    });

    return { candidates, companyLabels, productLabels };
  }
}
